import type { Router } from '@bulthaup/api/router';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { FC, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import Decimal from 'decimal.js';
import superjson from 'superjson';

import { env } from '../env';

// Use Uint8Array instead of Buffer for browser compatibility
superjson.registerCustom<Uint8Array, number[]>(
  {
    isApplicable: (v): v is Uint8Array => v instanceof Uint8Array,
    serialize: v => [...v],
    deserialize: v => new Uint8Array(v),
  },
  'buffer',
);

superjson.registerCustom<Decimal, string>(
  {
    isApplicable: (v): v is Decimal => Decimal.isDecimal(v),
    serialize: v => v.toString(),
    deserialize: v => new Decimal(v),
  },
  'decimal.js',
);

// eslint-disable-next-line react-refresh/only-export-components
export const trpc = createTRPCReact<Router>({
  overrides: {
    useMutation: {
      async onSuccess(opts) {
        await opts.originalFn();
        await opts.queryClient.invalidateQueries();
      },
    },
  },
});

export type RouterInput = inferRouterInputs<Router>;
export type RouterOutput = inferRouterOutputs<Router>;

export const TRPCProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: env.VITE_SERVER_URL + '/trpc',
          transformer: superjson,
          fetch(url, options) {
            return fetch(url, {
              ...(options as RequestInit),
              credentials: 'include',
            });
          },
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};
